import React, { FC, useEffect } from 'react';
import { useLocation, Navigate, Outlet } from 'react-router';

import { backendApi } from 'src/api';
import { useFetchTokenMutation } from 'src/api/authentication-api/authentication-api';
import { Spinner } from 'src/components/spinner';
import { NavigationService } from 'src/services/navigation-service';
import { resetStateForNewAccountActionCreator } from 'src/state/account/action-creators/reset-state-for-new-account-action-creator';
import { selectSelectedAccount } from 'src/state/account/selectors';
import { startAuthenticationActionCreator } from 'src/state/auth/action-creators/start-authentication-action-creator';
import { selectReachedStep } from 'src/state/order/selectors';
import { OrderStep } from 'src/state/order/types';
import { useAppDispatch, useAppSelector } from 'src/state/thunk-api';
import { useSelectSubmitShoppingCartState } from 'src/hooks/use-submit-shopping-cart-mutation';

export const AuthenticatedRoute: FC = () => {
  const [, { isUninitialized, isError, isSuccess, isLoading }] = useFetchTokenMutation();
  const {
    isLoading: isSubmitting,
    isSuccess: submitSuccess,
    isError: submitError,
  } = useSelectSubmitShoppingCartState();
  const selectedAccount = useAppSelector(selectSelectedAccount);
  const reachedStep = useAppSelector(selectReachedStep);
  const dispatch = useAppDispatch();
  const path = useLocation().pathname;
  const submitCompleted = submitSuccess || submitError;
  const leavingSuccessPage = submitCompleted && reachedStep === OrderStep.SUCCESS && path !== NavigationService.SUCCESS;
  const leavingSuccessPageWhilePending =
    isSubmitting && reachedStep === OrderStep.SUCCESS && path !== NavigationService.SUCCESS;

  useEffect(() => {
    if (isUninitialized) {
      dispatch(startAuthenticationActionCreator());
    }
  }, [isUninitialized, dispatch]);

  useEffect(() => {
    if (leavingSuccessPage) {
      dispatch(backendApi.util.resetApiState());
      dispatch(resetStateForNewAccountActionCreator());
    }
  }, [dispatch, leavingSuccessPage]);

  if (isError) {
    return <Navigate to={NavigationService.ERROR} replace={true} />;
  }

  if (!isSuccess || (!!selectedAccount && isLoading) || leavingSuccessPage || leavingSuccessPageWhilePending) {
    return <Spinner />;
  }

  return <Outlet />;
};
